import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';

// Fallback when the project has a logo but never saved a placement.
const DEFAULT_PLACEMENT = { x: 0.88, y: 0.88, size: 0.1 };
const MIN_SIZE = 0.05;
const MAX_SIZE = 0.5;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

export const initialPlacement = (overlay) => {
  const has = overlay?.logo_x_percent != null
    && overlay?.logo_y_percent != null
    && overlay?.logo_size_percent != null;
  return has
    ? { x: Number(overlay.logo_x_percent), y: Number(overlay.logo_y_percent), size: Number(overlay.logo_size_percent) }
    : DEFAULT_PLACEMENT;
};

// Keep the whole logo inside the image (logoH = logo height / image height).
export const fitPlacement = ({ x, y, size }, logoH) => ({
  size,
  x: clamp(x, 0, Math.max(0, 1 - size)),
  y: clamp(y, 0, Math.max(0, 1 - logoH)),
});

/** FormData fields understood by POST /upload/image and /content/{id}/upload-image. */
export const appendLogoFields = (formData, placement) => {
  if (!placement) return formData;
  formData.append('logo_x', String(placement.logo_x));
  formData.append('logo_y', String(placement.logo_y));
  formData.append('logo_size', String(placement.logo_size));
  return formData;
};

function LogoPromptDialog({ file, overlay, onResolve }) {
  const [step, setStep] = useState('ask'); // 'ask' | 'edit'
  const [placement, setPlacement] = useState(() => initialPlacement(overlay));
  const [imgAspect, setImgAspect] = useState(1); // w / h of the uploaded image
  const [logoAspect, setLogoAspect] = useState(1); // h / w of the logo
  const wrapRef = useRef(null);
  const drag = useRef(null);

  // Create AND revoke inside the effect: with useMemo, React StrictMode (dev) runs
  // the cleanup once and the revoked blob URL leaves a broken image.
  const [previewUrl, setPreviewUrl] = useState('');
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // logo height as a fraction of the image height
  const logoH = placement.size * logoAspect * imgAspect;
  const shown = fitPlacement(placement, logoH);

  const onDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, x: shown.x, y: shown.y };
  };
  const onMove = (e) => {
    const d = drag.current;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!d || !rect) return;
    const next = {
      ...placement,
      x: d.x + (e.clientX - d.sx) / rect.width,
      y: d.y + (e.clientY - d.sy) / rect.height,
    };
    setPlacement(fitPlacement(next, logoH));
  };
  const onUp = () => { drag.current = null; };

  const setSize = (size) => setPlacement((p) => ({ ...p, size }));

  const save = () => {
    const f = fitPlacement(placement, logoH);
    onResolve({
      logo_x: Number(f.x.toFixed(4)),
      logo_y: Number(f.y.toFixed(4)),
      logo_size: Number(f.size.toFixed(4)),
    });
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onResolve(undefined); }}>
      <DialogContent className="bg-[#0F1D32] border-[#1A2B42] text-white max-w-3xl" data-testid="logo-upload-dialog">
        {step === 'ask' ? (
          <>
            <DialogHeader>
              <DialogTitle>Logo auf das Bild legen?</DialogTitle>
              <DialogDescription className="text-[#A0AEC0]">
                {file.name}
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                data-testid="logo-prompt-no"
                onClick={() => onResolve(null)}
                className="border-[#1A2B42] text-[#A0AEC0] bg-transparent hover:bg-[#1A2B42]"
              >
                Nein
              </Button>
              <Button
                type="button"
                data-testid="logo-prompt-yes"
                onClick={() => setStep('edit')}
                className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00D9C8]/90"
              >
                Ja
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Logo platzieren</DialogTitle>
              <DialogDescription className="text-[#A0AEC0]">
                Ziehe das Logo an die gewünschte Stelle und stelle die Größe ein.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center bg-[#0A1628] rounded-lg p-2">
              <div ref={wrapRef} className="relative inline-block select-none leading-none" data-testid="logo-edit-area">
                {previewUrl && <img
                  src={previewUrl}
                  alt=""
                  draggable={false}
                  onLoad={(e) => {
                    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                    if (w && h) setImgAspect(w / h);
                  }}
                  className="block rounded"
                  style={{ maxHeight: '60vh', maxWidth: '100%', minHeight: 120 }}
                />}
                <div
                  data-testid="logo-draggable"
                  onPointerDown={onDown}
                  onPointerMove={onMove}
                  onPointerUp={onUp}
                  onPointerCancel={onUp}
                  className="absolute cursor-move touch-none outline outline-1 outline-[#00D9C8]/70"
                  style={{ left: `${shown.x * 100}%`, top: `${shown.y * 100}%`, width: `${shown.size * 100}%` }}
                >
                  <img
                    src={overlay.logo_url}
                    alt="Logo"
                    draggable={false}
                    onLoad={(e) => {
                      const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                      if (w && h) setLogoAspect(h / w);
                    }}
                    className="block w-full h-auto pointer-events-none"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-[#A0AEC0] shrink-0">Größe</span>
              <input
                type="range"
                data-testid="logo-size-range"
                min={MIN_SIZE * 100}
                max={MAX_SIZE * 100}
                step={1}
                value={Math.round(placement.size * 100)}
                onChange={(e) => setSize(Number(e.target.value) / 100)}
                className="flex-1 accent-[#00D9C8]"
              />
              <span className="text-xs text-[#5A6C7D] w-9 text-right">{Math.round(placement.size * 100)}%</span>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                data-testid="logo-edit-cancel"
                onClick={() => onResolve(undefined)}
                className="border-[#1A2B42] text-[#A0AEC0] bg-transparent hover:bg-[#1A2B42]"
              >
                Abbrechen
              </Button>
              <Button
                type="button"
                data-testid="logo-edit-save"
                onClick={save}
                className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00D9C8]/90"
              >
                Speichern
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Ask "Logo auf das Bild legen?" before an image is uploaded.
 *
 *   const { askLogo, logoDialog } = useLogoPrompt({ apiBase, getAuthHeader });
 *   const placement = await askLogo(file);
 *   if (placement === undefined) continue;           // user cancelled → skip this file
 *   appendLogoFields(formData, placement);           // null → plain upload as before
 *   ... render {logoDialog} once in the page.
 *
 * Only asked when the logo overlay is switched on in the settings and a logo exists;
 * otherwise resolves null → upload as before.
 */
export function useLogoPrompt({ apiBase, getAuthHeader }) {
  const [pending, setPending] = useState(null); // { file, overlay, resolve }
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;

  const askLogo = useCallback(async (file) => {
    let overlay = null;
    try {
      const r = await axios.get(`${apiBase}/settings/overlay`, { headers: authRef.current() });
      overlay = r.data;
    } catch (err) {
      return null;
    }
    // Nur fragen, wenn das Logo-Overlay in den Einstellungen aktiv ist.
    if (!overlay?.overlay_enabled || !overlay?.logo_url) return null;
    return new Promise((resolve) => setPending({ file, overlay, resolve }));
  }, [apiBase]);

  const finish = (value) => {
    pending?.resolve(value);
    setPending(null);
  };

  const logoDialog = pending ? (
    <LogoPromptDialog key={pending.file.name + pending.file.size} file={pending.file} overlay={pending.overlay} onResolve={finish} />
  ) : null;

  return { askLogo, logoDialog };
}

export default LogoPromptDialog;
